<template>
  <div class="chat-page nav-content-inset">
    <div class="chat-card">
      <header class="chat-head">
        <el-button v-if="peerUuid" text @click="router.push('/chat')">会话</el-button>
        <h2>{{ peerUuid ? (peerName || '聊天') : '聊天' }}</h2>
        <span v-if="!peerUuid" class="chat-hint">只有互相喜欢的人可以聊天</span>
      </header>

      <div v-if="!peerUuid" class="inbox" v-loading="loading">
        <p v-if="!loading && threads.length === 0" class="empty">还没有会话。在「喜欢 → 互相喜欢」里点「发消息」开始。</p>
        <button v-for="item in threads" :key="item.peerUuid" type="button" class="thread" @click="openThread(item.peerUuid)">
          <span class="thread-name">{{ item.peerNickname }}</span>
          <span class="thread-text">{{ item.lastContent }}</span>
          <span v-if="item.unread" class="thread-unread">{{ item.unread }}</span>
        </button>
      </div>

      <template v-else>
        <div class="thread-body" ref="scroller" v-loading="loading">
          <p v-if="blocked" class="empty">{{ blocked }}</p>
          <div v-for="msg in messages" :key="msg.messageId" class="bubble-row" :class="{ mine: msg.mine }">
            <div class="bubble">
              <p>{{ msg.content }}</p>
              <span class="bubble-time">{{ formatChatTime(msg.createdAt) }}</span>
              <button v-if="!msg.mine" type="button" class="report" @click="openReport(msg)">举报</button>
            </div>
          </div>
        </div>
        <form v-if="!blocked" class="composer" @submit.prevent="send">
          <el-input v-model="draft" type="textarea" :rows="2" maxlength="500" placeholder="写一条消息，最多 500 字" />
          <el-button type="primary" native-type="submit" :loading="sending" :disabled="!draft.trim()">发送</el-button>
        </form>
      </template>
    </div>
    <el-dialog v-model="reportOpen" title="举报这条消息" width="360px">
      <div class="report-presets">
        <el-button v-for="reason in reportReasons" :key="reason" @click="reportReason = reason">{{ reason }}</el-button>
      </div>
      <el-input v-model="reportReason" maxlength="200" placeholder="也可以自己写原因" />
      <template #footer>
        <el-button @click="reportOpen = false">取消</el-button>
        <el-button type="primary" :disabled="!reportReason.trim()" @click="submitReport">提交</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { authHeaders } from '@/utils/http'

const REPORT_REASONS = ['骚扰', '广告或引流', '不当内容', '冒充他人']

export default {
  name: 'FriendChatView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const loading = ref(false)
    const sending = ref(false)
    const threads = ref([])
    const messages = ref([])
    const peerName = ref('')
    const peerUuid = ref('')
    const blocked = ref('')
    const draft = ref('')
    const scroller = ref(null)
    const reportOpen = ref(false)
    const reportReason = ref('')
    const reportMessageId = ref(0)
    const reportReasons = REPORT_REASONS
    let pollTimer = 0

    const formatChatTime = (ts) => {
      const n = Number(ts)
      if (!n) return ''
      const d = new Date(n * 1000)
      const p = (x) => String(x).padStart(2, '0')
      return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    }

    const publishUnread = (list) => {
      const unread = (list || []).reduce((sum, item) => sum + (Number(item.unread) || 0), 0)
      window.dispatchEvent(new CustomEvent('chat-unread-changed', { detail: { unread } }))
    }

    const scrollDown = async () => {
      await nextTick()
      const el = scroller.value
      if (el) el.scrollTop = el.scrollHeight
    }

    const loadInbox = async (silent) => {
      if (!silent) loading.value = true
      try {
        const res = await axios.post('/api/v1/message/inbox', {}, { headers: authHeaders() })
        if (res.data.code === 200) {
          threads.value = res.data.data?.threads || []
          publishUnread(threads.value)
        } else if (!silent) {
          ElMessage.error(res.data.message || '读取会话失败')
        }
      } catch (e) {
        if (!silent) ElMessage.error('读取会话失败')
      } finally {
        if (!silent) loading.value = false
      }
    }

    const loadThread = async (silent) => {
      if (!silent) {
        loading.value = true
        blocked.value = ''
      }
      try {
        const res = await axios.post('/api/v1/message/thread', { peerUuid: peerUuid.value, page: 1, pageSize: 50 }, { headers: authHeaders() })
        if (res.data.code === 200) {
          const next = res.data.data?.messages || []
          const grew = next.length > messages.value.length
          messages.value = next
          peerName.value = res.data.data?.peerNickname || ''
          if (!silent || grew) await scrollDown()
        } else if (!silent) {
          messages.value = []
          blocked.value = res.data.message || '暂时不能聊天'
        }
      } catch (e) {
        if (!silent) blocked.value = '暂时不能聊天'
      } finally {
        if (!silent) loading.value = false
      }
    }

    const send = async () => {
      const content = draft.value.trim()
      if (!content) return
      sending.value = true
      try {
        const res = await axios.post('/api/v1/message/send', { peerUuid: peerUuid.value, content }, { headers: authHeaders() })
        if (res.data.code === 200) {
          draft.value = ''
          await loadThread()
        } else {
          ElMessage.error(res.data.message || '发送失败')
        }
      } catch (e) {
        ElMessage.error('发送失败')
      } finally {
        sending.value = false
      }
    }

    const openReport = (msg) => {
      reportMessageId.value = msg.messageId
      reportReason.value = ''
      reportOpen.value = true
    }

    const submitReport = async () => {
      const reason = reportReason.value.trim()
      if (!reason) return
      try {
        const res = await axios.post('/api/v1/message/report', { messageId: reportMessageId.value, reason }, { headers: authHeaders() })
        if (res.data.code === 200) {
          ElMessage.success(res.data.message || '已提交')
          reportOpen.value = false
        } else {
          ElMessage.error(res.data.message || '举报失败')
        }
      } catch (e) {
        ElMessage.error('举报失败')
      }
    }

    const openThread = (uuid) => router.push(`/chat/${uuid}`)

    const syncRoute = () => {
      peerUuid.value = String(route.params.uuid || '')
      if (peerUuid.value) loadThread()
      else loadInbox()
    }

    watch(() => route.params.uuid, syncRoute)
    onMounted(() => {
      syncRoute()
      pollTimer = window.setInterval(() => {
        if (peerUuid.value) loadThread(true)
        else loadInbox(true)
      }, 8000)
    })
    onBeforeUnmount(() => {
      if (pollTimer) window.clearInterval(pollTimer)
    })

    return {
      router, loading, sending, threads, messages, peerName, peerUuid, blocked, draft, scroller,
      send, openReport, submitReport, openThread, formatChatTime, reportOpen, reportReason, reportReasons
    }
  }
}
</script>

<style scoped>
.chat-page { max-width: 760px; margin: 0 auto; padding: 16px; }
.chat-card { background: #fff; border-radius: 12px; min-height: 70vh; display: flex; flex-direction: column; box-shadow: 0 1px 4px rgba(0,0,0,.08); }
.chat-head { display: flex; align-items: center; gap: 8px; padding: 12px 16px; border-bottom: 1px solid var(--el-border-color-lighter); }
.chat-head h2 { margin: 0; font-size: 18px; }
.chat-hint { margin-left: auto; color: var(--el-text-color-secondary); font-size: 13px; }
.inbox { padding: 8px 0; }
.thread { width: 100%; display: grid; grid-template-columns: 1fr auto; gap: 4px 12px; text-align: left; padding: 12px 16px; border: 0; background: transparent; border-bottom: 1px solid var(--el-border-color-lighter); cursor: pointer; }
.thread-name { font-weight: 600; }
.thread-text { grid-column: 1; color: var(--el-text-color-secondary); font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.thread-unread { grid-row: 1 / span 2; align-self: center; background: var(--el-color-danger); color: #fff; border-radius: 10px; min-width: 20px; padding: 0 6px; font-size: 12px; text-align: center; }
.thread-body { flex: 1; overflow-y: auto; padding: 16px; max-height: 60vh; }
.bubble-row { display: flex; margin-bottom: 10px; }
.bubble-row.mine { justify-content: flex-end; }
.bubble { max-width: 75%; background: var(--el-fill-color-light); border-radius: 12px; padding: 8px 12px; }
.mine .bubble { background: var(--el-color-primary-light-8); }
.bubble p { margin: 0; white-space: pre-wrap; word-break: break-word; }
.bubble-time { display: block; margin-top: 4px; color: var(--el-text-color-secondary); font-size: 12px; }
.report-presets { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.report { border: 0; background: transparent; color: var(--el-text-color-secondary); font-size: 12px; padding: 4px 0 0; cursor: pointer; }
.composer { display: flex; gap: 8px; align-items: flex-end; padding: 12px; border-top: 1px solid var(--el-border-color-lighter); }
.empty { color: var(--el-text-color-secondary); text-align: center; padding: 32px 16px; }
</style>
